"use client";

import { useReducer, useState, useEffect, useRef, useCallback } from "react";
import { m, AnimatePresence } from "motion/react";
import {
  BrainCircuit,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  Mail,
  CheckCircle2,
  Sparkles,
  Target,
  TrendingUp,
  Download,
  ShieldCheck,
} from "lucide-react";
import Link from "next/link";
import dynamic from "next/dynamic";
import BlueprintBackground from "@/components/BlueprintBackground";
import MetadataUpdater from "@/components/MetadataUpdater";
import { withContactDetails } from "@/data/knowledge/company";
import { useTranslation } from "@/lib/i18n";
import { trackEvent } from "@/lib/tracking";
import type { TranslationKey } from "@/lib/translations";

const AIReadinessInsights = dynamic(() => import("@/components/AIReadinessInsights"), { ssr: false });
const AIReadinessShareButtons = dynamic(() => import("@/components/AIReadinessShareButtons"), { ssr: false });

// --- Types ---

type WizardPhase = "intro" | "questions" | "results";
type EmailStatus = "idle" | "sending" | "success" | "error";

interface WizardState {
  phase: WizardPhase;
  currentStep: number;
  answers: (number | null)[];
  /** Score from a shared result link (?score=X&t=Y); null for an own run. */
  sharedScore: number | null;
}

type WizardAction =
  | { type: "START" }
  | { type: "ANSWER"; value: number }
  | { type: "NEXT" }
  | { type: "BACK" }
  | { type: "RESTART" }
  | { type: "RESTORE"; state: WizardState };

// --- Question Data ---

interface Question {
  titleKey: TranslationKey;
  options: { labelKey: TranslationKey; value: number }[];
}

const questions: Question[] = [
  {
    titleKey: "aicheck.q1.title",
    options: [
      { labelKey: "aicheck.q1.opt1", value: 1 },
      { labelKey: "aicheck.q1.opt2", value: 2 },
      { labelKey: "aicheck.q1.opt3", value: 3 },
      { labelKey: "aicheck.q1.opt4", value: 4 },
      { labelKey: "aicheck.q1.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q2.title",
    options: [
      { labelKey: "aicheck.q2.opt1", value: 1 },
      { labelKey: "aicheck.q2.opt2", value: 2 },
      { labelKey: "aicheck.q2.opt3", value: 3 },
      { labelKey: "aicheck.q2.opt4", value: 4 },
      { labelKey: "aicheck.q2.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q3.title",
    options: [
      { labelKey: "aicheck.q3.opt1", value: 1 },
      { labelKey: "aicheck.q3.opt2", value: 2 },
      { labelKey: "aicheck.q3.opt3", value: 3 },
      { labelKey: "aicheck.q3.opt4", value: 4 },
      { labelKey: "aicheck.q3.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q4.title",
    options: [
      { labelKey: "aicheck.q4.opt1", value: 1 },
      { labelKey: "aicheck.q4.opt2", value: 2 },
      { labelKey: "aicheck.q4.opt3", value: 3 },
      { labelKey: "aicheck.q4.opt4", value: 4 },
      { labelKey: "aicheck.q4.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q5.title",
    options: [
      { labelKey: "aicheck.q5.opt1", value: 1 },
      { labelKey: "aicheck.q5.opt2", value: 2 },
      { labelKey: "aicheck.q5.opt3", value: 3 },
      { labelKey: "aicheck.q5.opt4", value: 4 },
      { labelKey: "aicheck.q5.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q6.title",
    options: [
      { labelKey: "aicheck.q6.opt1", value: 1 },
      { labelKey: "aicheck.q6.opt2", value: 2 },
      { labelKey: "aicheck.q6.opt3", value: 3 },
      { labelKey: "aicheck.q6.opt4", value: 4 },
      { labelKey: "aicheck.q6.opt5", value: 5 },
    ],
  },
  {
    titleKey: "aicheck.q7.title",
    options: [
      { labelKey: "aicheck.q7.opt1", value: 1 },
      { labelKey: "aicheck.q7.opt2", value: 2 },
      { labelKey: "aicheck.q7.opt3", value: 3 },
      { labelKey: "aicheck.q7.opt4", value: 4 },
      { labelKey: "aicheck.q7.opt5", value: 5 },
    ],
  },
];

// --- Scoring ---

interface TierInfo {
  titleKey: TranslationKey;
  descKey: TranslationKey;
  serviceKey: TranslationKey;
  serviceHref: string;
  roi: { min: number; max: number };
}

const tiers: TierInfo[] = [
  {
    titleKey: "aicheck.result.tier1.title",
    descKey: "aicheck.result.tier1.desc",
    serviceKey: "aicheck.result.tier1.service",
    serviceHref: "/solutions/consulting",
    roi: { min: 5, max: 15 },
  },
  {
    titleKey: "aicheck.result.tier2.title",
    descKey: "aicheck.result.tier2.desc",
    serviceKey: "aicheck.result.tier2.service",
    serviceHref: "/solutions/ai-automation",
    roi: { min: 15, max: 30 },
  },
  {
    titleKey: "aicheck.result.tier3.title",
    descKey: "aicheck.result.tier3.desc",
    serviceKey: "aicheck.result.tier3.service",
    serviceHref: "/solutions/ai-automation",
    roi: { min: 25, max: 50 },
  },
  {
    titleKey: "aicheck.result.tier4.title",
    descKey: "aicheck.result.tier4.desc",
    serviceKey: "aicheck.result.tier4.service",
    serviceHref: "/solutions/ai-automation",
    roi: { min: 40, max: 70 },
  },
];

function calculateScore(answers: number[]): number {
  const rawSum = answers.reduce((sum, a) => sum + a, 0);
  return Math.round(((rawSum - 7) / 28) * 100);
}

function getTier(score: number): TierInfo {
  if (score <= 25) return tiers[0];
  if (score <= 50) return tiers[1];
  if (score <= 75) return tiers[2];
  return tiers[3];
}

function getTierIndex(score: number): number {
  if (score <= 25) return 0;
  if (score <= 50) return 1;
  if (score <= 75) return 2;
  return 3;
}

// Build question title and option keys for insights component
const questionTitleKeys: TranslationKey[] = questions.map((q) => q.titleKey);
const optionLabelKeys: TranslationKey[][] = questions.map((q) =>
  q.options.map((o) => o.labelKey)
);

// --- Reducer ---

const initialState: WizardState = {
  phase: "intro",
  currentStep: 0,
  answers: Array(7).fill(null),
  sharedScore: null,
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "START":
      return { ...state, phase: "questions", currentStep: 0 };
    case "ANSWER": {
      const newAnswers = [...state.answers];
      newAnswers[state.currentStep] = action.value;
      return { ...state, answers: newAnswers };
    }
    case "NEXT":
      if (state.currentStep >= 6) {
        return { ...state, phase: "results" };
      }
      return { ...state, currentStep: state.currentStep + 1 };
    case "BACK":
      if (state.currentStep <= 0) {
        return { ...state, phase: "intro" };
      }
      return { ...state, currentStep: state.currentStep - 1 };
    case "RESTART":
      return initialState;
    case "RESTORE":
      return action.state;
    default:
      return state;
  }
}

// --- Score Gauge Component ---

function ScoreGauge({ score }: { score: number }) {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75; // 270 degrees

  const filledLength = (score / 100) * arcLength;
  const [animatedScore, setAnimatedScore] = useState(0);
  // Derived so the zero case needs no state write from the effect body.
  const displayScore = score === 0 ? 0 : animatedScore;

  const color =
    score >= 76
      ? "var(--mint)"
      : score >= 51
        ? "var(--mint-text)"
        : score >= 26
          ? "#F59E0B"
          : "#EF4444";

  // Animated score counter
  useEffect(() => {
    if (score === 0) return;
    const delay = 800;
    const duration = 1500;
    let rafId: number;
    const start = performance.now();
    function tick(now: number) {
      const elapsed = now - start - delay;
      if (elapsed < 0) { rafId = requestAnimationFrame(tick); return; }
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOut cubic
      setAnimatedScore(Math.round(score * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [score]);

  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Background arc */}
        <circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke="var(--border-faint)"
          strokeWidth="10"
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(135 100 100)"
        />
        {/* Filled arc */}
        <m.circle
          cx="100"
          cy="100"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={`${arcLength} ${circumference}`}
          initial={{ strokeDashoffset: arcLength }}
          animate={{ strokeDashoffset: arcLength - filledLength }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
          strokeLinecap="round"
          transform="rotate(135 100 100)"
        />
      </svg>
      {/* Score text overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <m.span
          className="text-5xl font-heading text-text-primary"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          {displayScore}
        </m.span>
        <span className="text-[10px] font-mono text-text-faint tracking-widest">
          / 100
        </span>
      </div>
    </div>
  );
}

// --- Main Component ---

export default function AIReadinessPage() {
  const { t, locale } = useTranslation();
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [emailStatus, setEmailStatus] = useState<EmailStatus>("idle");
  const [emailForm, setEmailForm] = useState({ name: "", email: "" });
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [showConsentGate, setShowConsentGate] = useState(false);
  const [focus, setFocus] = useState<{ step: number; index: number }>({
    step: 0,
    index: -1,
  });
  const isSharedView = state.sharedScore !== null;
  const prevPhaseRef = useRef<WizardPhase>("intro");
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Restore from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("aicheck-progress");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.phase && Array.isArray(parsed.answers)) {
          dispatch({
            type: "RESTORE",
            state: {
              phase: parsed.phase,
              currentStep: parsed.currentStep ?? 0,
              answers: parsed.answers,
              sharedScore: null,
            },
          });
        }
      }
    } catch {
      /* ignore parse errors */
    }
  }, []);

  // Save to localStorage on state change (only with consent)
  useEffect(() => {
    if (state.phase !== "intro" && localStorage.getItem("cookie-consent") === "all") {
      localStorage.setItem(
        "aicheck-progress",
        JSON.stringify({
          phase: state.phase,
          currentStep: state.currentStep,
          answers: state.answers,
        })
      );
    }
  }, [state]);

  // Clear auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  // Focus resets implicitly when the step changes — no effect needed
  const focusedOption = focus.step === state.currentStep ? focus.index : -1;
  const setFocusedOption = useCallback(
    (index: number) => setFocus({ step: state.currentStep, index }),
    [state.currentStep]
  );

  // Keyboard handler for radio group
  const handleRadioGroupKeyDown = useCallback(
    (e: React.KeyboardEvent, optionCount: number) => {
      let newIndex = focusedOption;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        newIndex = focusedOption < optionCount - 1 ? focusedOption + 1 : 0;
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        newIndex = focusedOption > 0 ? focusedOption - 1 : optionCount - 1;
      }
      if (newIndex !== focusedOption) {
        setFocusedOption(newIndex);
        optionRefs.current[newIndex]?.focus();
      }
    },
    [focusedOption, setFocusedOption]
  );

  const score =
    state.phase === "results"
      ? (state.sharedScore ??
        calculateScore(state.answers.filter((a): a is number => a !== null)))
      : 0;
  const tier = getTier(score);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailForm.name || !emailForm.email) return;

    setEmailStatus("sending");
    try {
      const res = await fetch("/api/ai-readiness", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: emailForm.name,
          email: emailForm.email,
          score,
          tierTitle: t(tier.titleKey),
          tierDesc: t(tier.descKey),
          roiRange: `${tier.roi.min}–${tier.roi.max}%`,
          recommendedService: t(tier.serviceKey),
          serviceLink: tier.serviceHref,
          answers: state.answers,
          locale,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setEmailStatus("success");
      trackEvent({
        event: "aicheck_email_submitted",
        score,
        tier_index: getTierIndex(score),
      });
    } catch {
      setEmailStatus("error");
    }
  };

  const handleStart = useCallback(() => {
    const hasConsent = localStorage.getItem("cookie-consent") === "all";
    if (hasConsent) {
      dispatch({ type: "START" });
      trackEvent({ event: "aicheck_started" });
      return;
    }
    // Show inline consent gate
    setShowConsentGate(true);
  }, []);

  // Listen for consent updates to auto-start after approval from the gate
  useEffect(() => {
    if (!showConsentGate) return;
    function onConsent(e: Event) {
      const detail = (e as CustomEvent<{ type: "all" | "necessary" }>).detail;
      setShowConsentGate(false);
      if (detail.type === "all") {
        dispatch({ type: "START" });
        trackEvent({ event: "aicheck_started" });
      }
    }
    window.addEventListener("consentUpdated", onConsent);
    return () => window.removeEventListener("consentUpdated", onConsent);
  }, [showConsentGate]);

  const handleRestart = () => {
    trackEvent({
      event: "aicheck_restarted",
      previous_phase: state.phase,
      score: state.phase === "results" ? score : undefined,
    });
    localStorage.removeItem("aicheck-progress");
    dispatch({ type: "RESTART" });
    setEmailStatus("idle");
    setEmailForm({ name: "", email: "" });
    setPdfGenerating(false);
  };

  const handlePdfDownload = async () => {
    setPdfGenerating(true);
    try {
      const { generateAIReadinessPDF } = await import("@/lib/ai-readiness-pdf");
      const getInsightTier = (answer: number) =>
        answer <= 2 ? "low" : answer === 3 ? "mid" : "high";
      await generateAIReadinessPDF({
        score,
        tierTitle: t(tier.titleKey),
        tierDesc: t(tier.descKey),
        roiRange: `${tier.roi.min}–${tier.roi.max}%`,
        recommendation: t(tier.serviceKey),
        answers: state.answers,
        questionTitles: questions.map((q) => t(q.titleKey)),
        insights: state.answers.map((a, i) =>
          t(`aicheck.insight.q${i + 1}.${getInsightTier(a ?? 3)}` as TranslationKey)
        ),
        recommendations: state.answers.map((a, i) =>
          t(`aicheck.recommend.q${i + 1}.${getInsightTier(a ?? 3)}` as TranslationKey)
        ),
        locale,
      });
      trackEvent({
        event: "aicheck_pdf_downloaded",
        score,
        tier_index: getTierIndex(score),
      });
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
    setPdfGenerating(false);
  };

  // Handle share URL: ?score=X&t=Y for read-only results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedScoreParam = params.get("score");
    const sharedTier = params.get("t");
    if (sharedScoreParam && sharedTier) {
      const s = parseInt(sharedScoreParam, 10);
      if (!isNaN(s) && s >= 0 && s <= 100) {
        dispatch({
          type: "RESTORE",
          state: {
            phase: "results",
            currentStep: 6,
            answers: Array(7).fill(null),
            sharedScore: s,
          },
        });
      }
    }
  }, []);

  // Track quiz completion (only on organic phase transition, not shared views or restores)
  useEffect(() => {
    if (
      state.phase === "results" &&
      prevPhaseRef.current === "questions" &&
      !isSharedView
    ) {
      const s = calculateScore(state.answers.filter((a): a is number => a !== null));
      trackEvent({
        event: "aicheck_completed",
        score: s,
        tier_index: getTierIndex(s),
        answers: state.answers,
      });
    }
    prevPhaseRef.current = state.phase;
  }, [state.phase, state.answers, isSharedView]);

  // Track drop-off when user leaves mid-quiz
  useEffect(() => {
    let hasFired = false;

    function fireDropoff() {
      if (hasFired || state.phase !== "questions") return;
      hasFired = true;
      trackEvent({
        event: "aicheck_dropoff",
        step: state.currentStep + 1,
        answers_so_far: state.answers,
        answered_count: state.answers.filter((a) => a !== null).length,
      });
    }

    function handleBeforeUnload() {
      fireDropoff();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") fireDropoff();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [state.phase, state.currentStep, state.answers]);

  return (
    <>
    <MetadataUpdater
      titleKey="meta.aiReadiness.title"
      descriptionKey="meta.aiReadiness.description"
    />
    <BlueprintBackground />
    <main className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-3xl mx-auto">
        <AnimatePresence mode="wait">
          {/* === INTRO === */}
          {state.phase === "intro" && (
            <m.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-mint-text/30 text-mint-text text-[10px] font-mono tracking-[0.3em] uppercase mb-8">
                <BrainCircuit size={14} />
                {t("aicheck.label")}
              </div>

              <h1 className="text-4xl md:text-5xl font-heading text-text-primary mb-6">
                {t("aicheck.title")}
              </h1>

              <p className="text-text-muted max-w-xl mx-auto mb-4 leading-relaxed">
                {t("aicheck.description")}
              </p>

              <p className="text-[10px] font-mono text-text-faint tracking-widest uppercase mb-10">
                {t("aicheck.duration")}
              </p>

              <AnimatePresence mode="wait">
                {showConsentGate ? (
                  <m.div
                    key="consent-gate"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-lg mx-auto"
                  >
                    <div className="p-6 border border-mint-text/20 bg-bg-card">
                      <ShieldCheck className="mx-auto mb-4 text-mint-text" size={36} />
                      <h3 className="font-bold text-text-primary mb-2">
                        {t("cookieGate.title")}
                      </h3>
                      <p className="text-sm text-text-muted mb-4 leading-relaxed">
                        {t("cookieGate.description")}
                      </p>
                      <p className="text-text-faint text-xs mb-6">
                        {t("cookieGate.privacy")}{" "}
                        <Link href="/datenschutz" className="text-mint-text hover:underline">
                          {t("cookieGate.privacyLink")}
                        </Link>
                      </p>
                      <button
                        onClick={() => {
                          localStorage.setItem("cookie-consent", "all");
                          window.dispatchEvent(new CustomEvent("consentUpdated", { detail: { type: "all" } }));
                        }}
                        className="px-8 py-4 bg-mint text-black font-black tracking-widest uppercase text-sm hover:bg-mint/90 transition-colors focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                      >
                        {t("cookieGate.acceptButton")}
                      </button>
                    </div>
                  </m.div>
                ) : (
                  <m.div
                    key="start-button"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <button
                      onClick={handleStart}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-mint text-black font-black tracking-widest uppercase text-sm hover:bg-mint/90 transition-colors focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                    >
                      {t("aicheck.cta.start")}
                      <ArrowRight size={18} />
                    </button>
                  </m.div>
                )}
              </AnimatePresence>
            </m.div>
          )}

          {/* === QUESTIONS === */}
          {state.phase === "questions" && (
            <m.div
              key="questions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Progress bar */}
              <div className="mb-2">
                <div className="w-full h-1 bg-border-faint">
                  <m.div
                    className="h-full bg-mint"
                    animate={{
                      width: `${(state.answers.filter((a) => a !== null).length / 7) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
              <div className="text-[10px] font-mono text-text-faint tracking-widest uppercase mb-10">
                {t("aicheck.progress.question")} {state.currentStep + 1}{" "}
                {t("aicheck.progress.of")} 7
              </div>

              {/* Question card with AnimatePresence */}
              <AnimatePresence mode="wait">
                <m.div
                  key={state.currentStep}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2
                    id={`question-${state.currentStep}`}
                    className="text-xl md:text-2xl font-bold text-text-primary mb-8 leading-relaxed"
                  >
                    {t(questions[state.currentStep].titleKey)}
                  </h2>

                  <div
                    role="radiogroup"
                    aria-labelledby={`question-${state.currentStep}`}
                    className="space-y-3"
                    onKeyDown={(e) =>
                      handleRadioGroupKeyDown(
                        e,
                        questions[state.currentStep].options.length
                      )
                    }
                  >
                    {questions[state.currentStep].options.map(
                      (option, optIdx) => {
                        const isSelected =
                          state.answers[state.currentStep] === option.value;
                        const isFocused = focusedOption === optIdx;
                        return (
                          <button
                            key={option.value}
                            ref={(el) => {
                              optionRefs.current[optIdx] = el;
                            }}
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={
                              isFocused || (focusedOption === -1 && optIdx === 0)
                                ? 0
                                : -1
                            }
                            onFocus={() => setFocusedOption(optIdx)}
                            onClick={() => {
                              dispatch({
                                type: "ANSWER",
                                value: option.value,
                              });
                              trackEvent({
                                event: "aicheck_question_answered",
                                question_number: state.currentStep + 1,
                                question_key:
                                  questions[state.currentStep].titleKey,
                                answer_value: option.value,
                                answer_key: option.labelKey,
                              });
                              // Auto-advance after selection (except last question)
                              if (autoAdvanceTimer.current)
                                clearTimeout(autoAdvanceTimer.current);
                              if (
                                state.currentStep < 6 &&
                                !window.matchMedia(
                                  "(prefers-reduced-motion: reduce)"
                                ).matches
                              ) {
                                autoAdvanceTimer.current = setTimeout(() => {
                                  trackEvent({
                                    event: "aicheck_question_navigated",
                                    direction: "next",
                                    from_step: state.currentStep + 1,
                                    to_step: state.currentStep + 2,
                                  });
                                  dispatch({ type: "NEXT" });
                                }, 600);
                              }
                            }}
                            className={`w-full text-left p-4 border transition-all focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none ${
                              isSelected
                                ? "border-mint-text bg-mint-text/10 text-text-primary"
                                : "border-border-faint bg-bg-card hover:border-mint-text/30 text-text-muted"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                  isSelected
                                    ? "border-mint-text"
                                    : "border-text-faint"
                                }`}
                              >
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-mint-text" />
                                )}
                              </div>
                              <span className="text-sm">
                                {t(option.labelKey)}
                              </span>
                            </div>
                          </button>
                        );
                      }
                    )}
                  </div>
                </m.div>
              </AnimatePresence>

              {/* Navigation buttons */}
              <div className="flex justify-between mt-10">
                <button
                  onClick={() => {
                    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
                    trackEvent({
                      event: "aicheck_question_navigated",
                      direction: "back",
                      from_step: state.currentStep + 1,
                      to_step: state.currentStep <= 0 ? "intro" : state.currentStep,
                    });
                    dispatch({ type: "BACK" });
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border-faint text-text-muted hover:text-text-primary hover:border-mint-text/30 transition-all text-xs font-bold tracking-widest uppercase focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                >
                  <ArrowLeft size={14} />
                  {t("aicheck.nav.back")}
                </button>
                <button
                  onClick={() => {
                    trackEvent({
                      event: "aicheck_question_navigated",
                      direction: "next",
                      from_step: state.currentStep + 1,
                      to_step: state.currentStep === 6 ? "results" : state.currentStep + 2,
                    });
                    dispatch({ type: "NEXT" });
                  }}
                  disabled={state.answers[state.currentStep] === null}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-mint text-black font-black tracking-widest uppercase text-xs hover:bg-mint/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                >
                  {state.currentStep === 6
                    ? t("aicheck.nav.finish")
                    : t("aicheck.nav.next")}
                  <ArrowRight size={14} />
                </button>
              </div>
            </m.div>
          )}

          {/* === RESULTS === */}
          {state.phase === "results" && (
            <m.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {/* Label + Title */}
              <div className="text-center mb-10">
                <div className="text-[10px] font-mono text-mint-text tracking-[0.3em] uppercase mb-4">
                  {t("aicheck.result.label")}
                </div>
                <h1 className="text-3xl md:text-4xl font-heading text-text-primary mb-8">
                  {t("aicheck.result.title")}
                </h1>

                {/* Score Gauge */}
                <ScoreGauge score={score} />
              </div>

              {/* Tier Card */}
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="p-6 border border-border-faint bg-bg-card mb-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles size={18} className="text-mint-text" />
                  <h3 className="font-bold text-text-primary uppercase tracking-wide">
                    {t(tier.titleKey)}
                  </h3>
                </div>
                <p className="text-sm text-text-muted leading-relaxed">
                  {t(tier.descKey)}
                </p>
              </m.div>

              {/* ROI + Recommendation Grid */}
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="grid md:grid-cols-2 gap-4 mb-8"
              >
                {/* ROI */}
                <div className="p-5 border border-border-faint bg-bg-card">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={16} className="text-mint-text" />
                    <span className="text-[10px] font-mono text-text-faint tracking-widest uppercase">
                      {t("aicheck.result.roi.label")}
                    </span>
                  </div>
                  <div className="text-2xl font-heading text-mint-text">
                    {tier.roi.min}–{tier.roi.max}%
                  </div>
                </div>

                {/* Recommendation */}
                <div className="p-5 border border-border-faint bg-bg-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={16} className="text-mint-text" />
                    <span className="text-[10px] font-mono text-text-faint tracking-widest uppercase">
                      {t("aicheck.result.recommendation.label")}
                    </span>
                  </div>
                  <div className="text-lg font-bold text-text-primary mb-2">
                    {t(tier.serviceKey)}
                  </div>
                  <Link
                    href={tier.serviceHref}
                    className="inline-flex items-center gap-2 text-xs font-bold text-mint-text tracking-widest uppercase hover:underline"
                  >
                    {t("aicheck.result.recommendation.cta")}
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </m.div>

              {/* Per-Question Insights (hidden for shared views) */}
              {!isSharedView && (
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                >
                  <AIReadinessInsights
                    answers={state.answers}
                    questionTitleKeys={questionTitleKeys}
                    optionLabelKeys={optionLabelKeys}
                  />
                </m.div>
              )}

              {/* Share Buttons */}
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.5 }}
              >
                <AIReadinessShareButtons score={score} tierIndex={getTierIndex(score)} />
              </m.div>

              {/* Shared view: Take assessment CTA */}
              {isSharedView && (
                <m.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1, duration: 0.5 }}
                  className="p-6 border border-mint-text/20 bg-bg-card mb-8 text-center"
                >
                  <h3 className="font-bold text-text-primary mb-2">
                    {t("aicheck.shared.title")}
                  </h3>
                  <p className="text-sm text-text-muted mb-5">
                    {t("aicheck.shared.desc")}
                  </p>
                  <Link
                    href="/ai-readiness"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-mint text-black font-black tracking-widest uppercase text-sm hover:bg-mint/90 transition-colors focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                  >
                    {t("aicheck.shared.cta")}
                    <ArrowRight size={18} />
                  </Link>
                </m.div>
              )}

              {/* Email Capture + PDF hint (hidden for shared views) */}
              {!isSharedView && <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.5 }}
                className="p-6 border border-mint-text/20 bg-bg-card mb-8"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Mail size={16} className="text-mint-text" />
                  <h3 className="font-bold text-text-primary">
                    {t("aicheck.email.title")}
                  </h3>
                </div>
                <p className="text-sm text-text-muted mb-2">
                  {t("aicheck.email.description")}
                </p>
                <p className="text-xs text-text-faint mb-5">
                  {t("aicheck.pdf.hint")}
                </p>

                {emailStatus === "success" ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-mint-text text-sm">
                      <CheckCircle2 size={16} />
                      {t("aicheck.email.success")}
                    </div>
                    <button
                      onClick={handlePdfDownload}
                      disabled={pdfGenerating}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-mint-text/50 text-mint-text font-bold tracking-widest uppercase text-xs hover:bg-mint-text/10 transition-colors disabled:opacity-50"
                    >
                      <Download size={14} />
                      {pdfGenerating ? t("aicheck.pdf.generating") : t("aicheck.pdf.download")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleEmailSubmit} className="space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-mono text-text-faint tracking-widest uppercase mb-1">
                          {t("aicheck.email.name")}
                        </label>
                        <input
                          type="text"
                          required
                          value={emailForm.name}
                          onChange={(e) =>
                            setEmailForm((f) => ({
                              ...f,
                              name: e.target.value,
                            }))
                          }
                          onBlur={() =>
                            trackEvent({
                              event: "aicheck_email_field_interaction",
                              field: "name",
                              has_value: emailForm.name.length > 0,
                            })
                          }
                          placeholder={t("aicheck.email.name.placeholder")}
                          className="w-full px-4 py-2.5 bg-bg-base border border-border-faint text-text-primary text-sm placeholder:text-text-faint focus:border-mint-text/50 focus:outline-none transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono text-text-faint tracking-widest uppercase mb-1">
                          {t("aicheck.email.email")}
                        </label>
                        <input
                          type="email"
                          required
                          value={emailForm.email}
                          onChange={(e) =>
                            setEmailForm((f) => ({
                              ...f,
                              email: e.target.value,
                            }))
                          }
                          onBlur={() =>
                            trackEvent({
                              event: "aicheck_email_field_interaction",
                              field: "email",
                              has_value: emailForm.email.length > 0,
                            })
                          }
                          placeholder={t("aicheck.email.email.placeholder")}
                          className="w-full px-4 py-2.5 bg-bg-base border border-border-faint text-text-primary text-sm placeholder:text-text-faint focus:border-mint-text/50 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    {emailStatus === "error" && (
                      <p className="text-red-500 text-xs">
                        {withContactDetails(t("aicheck.email.error"))}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={emailStatus === "sending"}
                      className="w-full px-6 py-3 bg-mint text-black font-black tracking-widest uppercase text-xs hover:bg-mint/90 transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base outline-none"
                    >
                      {emailStatus === "sending"
                        ? t("aicheck.email.sending")
                        : t("aicheck.email.submit")}
                    </button>
                  </form>
                )}
              </m.div>}

              {/* CTA block */}
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.5 }}
                className="bg-mint p-10 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 mb-8"
              >
                <div>
                  <h2 className="text-2xl sm:text-3xl font-heading font-black text-black uppercase tracking-tighter mb-2">
                    {t("aicheck.cta.title")}
                  </h2>
                  <p className="text-black/70 font-bold">
                    {t("aicheck.cta.desc")}
                  </p>
                </div>
                <Link
                  href="/#contact"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-black tracking-widest text-xs uppercase hover:bg-black/80 transition-colors shrink-0 focus-visible:ring-2 focus-visible:ring-mint-text focus-visible:ring-offset-2 outline-none"
                >
                  {t("aicheck.cta.button")}
                  <ArrowRight size={16} />
                </Link>
              </m.div>

              {/* Restart */}
              <div className="text-center">
                <button
                  onClick={handleRestart}
                  className="inline-flex items-center gap-2 text-[10px] font-mono text-text-faint uppercase tracking-widest hover:text-mint-text transition-colors"
                >
                  <RotateCcw size={12} />
                  {t("aicheck.restart")}
                </button>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </main>
    </>
  );
}
